import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { defaultStyles, JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Button } from "@/components/ui/button";
import { copyToClipboard } from "@/lib/clipboard";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import { useClaim } from "../api/use-claim";
import { PassengerTable } from "../components/passengers/passenger-table";
import { PassengerTableSkeleton } from "../components/passengers/passenger-table-skeleton";

/** Keeps every key expanded except `parsedHistory`, folded by default (large/noisy). */
function shouldExpandNode(_level: number, _value: unknown, field?: string) {
  return field !== "parsedHistory";
}

/** PNR history viewer — port of the json-viewer block of Passengers.vue (Upclaim only). */
function PnrHistoryViewer({ claimId }: { claimId: string }) {
  const [showPnr, setShowPnr] = useState(false);
  const { data } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "history"],
    queryFn: () => adminAPI.retrieveClaimHistory(claimId),
  });

  return (
    <div className="mt-4 text-left">
      <div className="flex justify-center gap-2 py-4">
        <Button onClick={() => setShowPnr((current) => !current)}>
          {!showPnr ? "Afficher le PNR" : "Masquer le PNR"}
        </Button>
        {showPnr ? (
          <Button variant="outline" onClick={() => void copyToClipboard(JSON.stringify(data ?? {}, null, 2))}>
            Copier le PNR
          </Button>
        ) : null}
      </div>
      {showPnr ? (
        <div className="min-h-max overflow-x-auto rounded-md border bg-gray-50 p-4 text-left text-xs">
          <JsonView data={data ?? {}} shouldExpandNode={shouldExpandNode} style={defaultStyles} />
        </div>
      ) : null}
    </div>
  );
}

export interface PassengersTabProps {
  claimId: string;
  variant?: "full" | "compact";
}

/** Port of pages/plugin/Passengers.vue. */
export function PassengersTab({ claimId }: PassengersTabProps) {
  const { data: claim } = useClaim(claimId);

  if (!claim) {
    return <PassengerTableSkeleton />;
  }

  return (
    <div>
      <PassengerTable claim={claim} />
      <Can permissions={UPCLAIM_ROLES}>
        <PnrHistoryViewer claimId={claimId} />
      </Can>
    </div>
  );
}
